import { useDraggable } from '@dnd-kit/core';
import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  useHover,
  useInteractions,
  useRole
} from '@floating-ui/react';
import { EdgeItemType } from 'data/types';
import { useGrabbingCursor } from 'hooks/use-grabbing-cursor';
import { useCallback, useState } from 'react';
import {
  EdgeRunType,
  OpeningEdgeType,
  OpeningPlacementType
} from 'utils/opening-position-types';
import { getEdgeItemTooltip } from 'utils/edge-item-labels';
import EdgeOpening from './edge-opening';

type DraggableEdgeItemProps = {
  item: EdgeItemType;
  edge: OpeningEdgeType;
  spaceId: string;
  offsetFt: number;
  run: EdgeRunType;
  placement: OpeningPlacementType | null;
  onContextMenu: (e: React.MouseEvent) => void;
  zoom?: number;
};

export default function DraggableEdgeItem({
  item,
  edge,
  spaceId,
  offsetFt,
  run,
  placement,
  onContextMenu,
  zoom = 1
}: DraggableEdgeItemProps) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `edge-item-${item.id}`,
      data: {
        type: item.type === 'door' ? 'grid-door' : 'grid-window',
        itemId: item.id,
        currentEdge: edge,
        spaceId,
        item
      }
    });

  useGrabbingCursor(isDragging);

  const { refs, floatingStyles, context } = useFloating({
    open: isTooltipOpen,
    onOpenChange: setIsTooltipOpen,
    placement: 'top',
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 5 })]
  });

  const hover = useHover(context, { move: false });
  const role = useRole(context, { role: 'tooltip' });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    role
  ]);

  const tooltipText = getEdgeItemTooltip(item);

  const refCallback = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      refs.setReference(node);
    },
    [setNodeRef, refs]
  );

  return (
    <>
      <EdgeOpening
        item={item}
        edge={edge}
        offsetFt={offsetFt}
        run={run}
        placement={placement}
        zoom={zoom}
        isDragging={isDragging}
        dragTransform={transform}
        interactive
        onContextMenu={onContextMenu}
        rootRef={refCallback}
        rootProps={{
          ...attributes,
          ...listeners,
          ...getReferenceProps()
        }}
      />
      <FloatingPortal>
        {isTooltipOpen && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className='z-50 rounded bg-[#444] px-2 py-1 text-sm text-white'
            {...getFloatingProps()}
          >
            {tooltipText}
          </div>
        )}
      </FloatingPortal>
    </>
  );
}
